import { isMatch, map, find, sortBy } from "lodash";
import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import Modal from "antd/lib/modal";
import { wrap as wrapDialog, DialogPropType } from "@/components/dialog-wrapper/DialogWrapper";
import {
  MappingType,
  ParameterMappingListInput,
  parameterMappingsToEditableMappings,
  editableMappingsToParameterMappings,
  synchronizeWidgetTitles,
} from "@/components/parameters/ParameterMappingInput";
import notification from "@/services/notification";
import { useTheme } from "@/components/ThemeProvider";
import "./EditParameterMappingsDialog.less";

export function getParamValuesSnapshot(mappings, dashboardParameters) {
  return map(
    sortBy(mappings, m => m.name),
    m => {
      let param;
      switch (m.type) {
        case MappingType.StaticValue:
          return [m.name, m.value];
        case MappingType.WidgetLevel:
          return [m.name, m.param.value];
        case MappingType.DashboardAddNew:
        case MappingType.DashboardMapToExisting:
          param = find(dashboardParameters, p => p.name === m.mapTo);
          return [m.name, param ? param.value : null];
        // no default
      }
    }
  );
}

function EditParameterMappingsDialog({ dashboard, widget, dialog }) {
  const { isDarkMode } = useTheme();

  const [saveInProgress, setSaveInProgress] = useState(false);
  const [parameterMappings, setParameterMappings] = useState(() =>
    parameterMappingsToEditableMappings(
      widget.options.parameterMappings,
      widget.query.getParametersDefs(),
      map(dashboard.getParametersDefs(), p => p.name)
    )
  );

  const originalParamValuesSnapshot = React.useMemo(
    () => getParamValuesSnapshot(parameterMappings, dashboard.getParametersDefs()),
    [] // Empty dependency array ensures this runs only once
  );

  const saveWidget = useCallback(() => {
    setSaveInProgress(true);

    const newMappings = editableMappingsToParameterMappings(parameterMappings);
    widget.options.parameterMappings = newMappings;

    const valuesChanged = !isMatch(
      originalParamValuesSnapshot,
      getParamValuesSnapshot(parameterMappings, dashboard.getParametersDefs())
    );

    const widgetsToSave = [
      widget,
      ...synchronizeWidgetTitles(widget.options.parameterMappings, dashboard.widgets),
    ];

    Promise.all(map(widgetsToSave, w => w.save()))
      .then(() => {
        dialog.close(valuesChanged);
      })
      .catch(() => {
        notification.error("Widget cannot be updated");
      })
      .finally(() => {
        setSaveInProgress(false);
      });
  }, [parameterMappings, widget, dashboard, dialog, originalParamValuesSnapshot]);

  const updateParamMappings = useCallback((newMappings) => {
    setParameterMappings(newMappings);
  }, []);

  return (
    <Modal
      {...dialog.props}
      title="Parameters"
      onOk={saveWidget}
      okButtonProps={{ loading: saveInProgress }}
      width={700}
      className={isDarkMode ? 'dark-mode' : ''}
    >
      {parameterMappings.length > 0 && (
        <ParameterMappingListInput
          mappings={parameterMappings}
          existingParams={dashboard.getParametersDefs()}
          onChange={updateParamMappings}
        />
      )}
    </Modal>
  );
}

EditParameterMappingsDialog.propTypes = {
  dashboard: PropTypes.object.isRequired,
  widget: PropTypes.object.isRequired,
  dialog: DialogPropType.isRequired,
};

export default wrapDialog(EditParameterMappingsDialog);
